import React, { useEffect, useState, useContext, useRef } from "react";
import { useDispatch } from "react-redux";
import { Alert} from "reactstrap";
import ReleaseTable from "../../components/releaseTable";
import Pagination from '../../components/pagination/pagination';
import {
  RELEASE_LIST_LAYOUT_CHANGED_EVENT,
  filterReleasesByPendingStatus,
  filterReleasesByReadyStatus,
  getDefaultPageSizeForLayout,
  getPageSizeOptionsForLayout,
  getReleaseListLayoutConfig,
  getReleaseListStatusQuery,
  isReleaseLayoutToggleEnabled,
  resolveReleaseListLayout,
} from "../../components/releaseTableHelpers";
import { useFetch } from "../../utils/fetchHook";
import { getCookie } from "../../jwt/_helpers/cookie";
import TableHelper from "../../components/tableHelper";
import ModuleAccessDeniedAlert from "../../components/moduleAccessDeniedAlert";
import {StateContext} from "../../utils/context";
import { isDemoLocked } from "../../utils/demoConfig";
import { getMergedDemoUser } from "../../utils/demoTwoFactorHelpers";
import { setReleases } from "../../redux/releases/action";

const Releases = () => {
  const dispatch = useDispatch();
  const token = getCookie("token");
  const {currentUser} = useContext(StateContext);
  const [forceUpdate, setForceUpdate] = useState(0);
  
  const didEff1 = useRef(false);
  const didEff2 = useRef(false);
  const [dataCount, setDataCount] = useState(0);
  const [currrentPage, setCurrentPage] = useState(1);
  const [layoutConfig, setLayoutConfig] = useState(getReleaseListLayoutConfig);
  const [layoutView, setLayoutView] = useState(() => resolveReleaseListLayout(layoutConfig));
  const [pageSize, setPageSize] = useState(() => getDefaultPageSizeForLayout(resolveReleaseListLayout(layoutConfig)));
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ordering, setOrdering] = useState({order: '-', name: 'official_date', sort: 'official_date'});
  const [subUser, setSubUser] = useState('');
  const [subUserEndpoint, setSubUserEndpoint] = useState('');
  const [parentUserOnly, setParentUserOnly] = useState('');
  const [todUrl, setTodUrl] = useState('#');
  const [touUrl, setTouUrl] = useState('#');
  const [ppUrl, setPpUrl] = useState('#');
  const [canAddTakedown, setCanAddTakedown] = useState(
    () => isDemoLocked() && getMergedDemoUser().can_add_takedown !== false,
  );

  const handleSearchQueryChange = value => {
    setCurrentPage(1);
    setQuery(value);
  };

  const statusQuery = getReleaseListStatusQuery(statusFilter);
  const [apiData, error, loading] = useFetch("GET", `releases/${subUserEndpoint}?ordering=${ordering.order + ordering.name}&page=${currrentPage}&page_size=${pageSize}&search=${encodeURIComponent(query)}${parentUserOnly}${statusQuery}`, token, false, forceUpdate);
  const [appVariables] = useFetch("GET", `app-variables/`, token, false, forceUpdate);
  const [tableData, setTableData] = useState([]);
 

  useEffect(() => {
    if (!isDemoLocked()) {
      return undefined;
    }
    const refreshReleases = () => {
      setForceUpdate(prev => prev + 1);
    };
    window.addEventListener("wl-demo-release-status-updated", refreshReleases);
    return () => {
      window.removeEventListener("wl-demo-release-status-updated", refreshReleases);
    };
  }, []);

  useEffect(() => {
    const handleLayoutConfigChange = (event) => {
      const nextConfig = event?.detail?.layoutConfig || getReleaseListLayoutConfig();
      const nextLayout = resolveReleaseListLayout(nextConfig);
      setLayoutConfig(nextConfig);
      setLayoutView(nextLayout);
      setPageSize(getDefaultPageSizeForLayout(nextLayout));
      setCurrentPage(1);
    };

    window.addEventListener(RELEASE_LIST_LAYOUT_CHANGED_EVENT, handleLayoutConfigChange);
    return () => {
      window.removeEventListener(RELEASE_LIST_LAYOUT_CHANGED_EVENT, handleLayoutConfigChange);
    };
  }, []);

  useEffect(() => {
    if (apiData?.detail === "Invalid page.") {
      setCurrentPage(1);
      return;
    }

    if (apiData && Array.isArray(apiData.results)) {
      let results = apiData.results;
      const userContext = currentUser
        ? {
            username: currentUser.username,
            is_premium_user: currentUser.is_premium_user,
            is_employee_account: currentUser.is_employee_account,
          }
        : null;

      if (statusFilter === "pending" && userContext) {
        results = filterReleasesByPendingStatus(results, userContext);
        setDataCount(results.length);
      } else if (statusFilter === "ready" && userContext) {
        results = filterReleasesByReadyStatus(results, userContext);
        setDataCount(results.length);
      } else {
        setDataCount(
          typeof apiData.count === "number" ? apiData.count : results.length,
        );
      }

      setTableData(results);
      dispatch(setReleases(results));
    }
  }, [apiData, dispatch, statusFilter, currentUser]);

  useEffect(() => {
    if (appVariables) {
      if(appVariables) {
        setTodUrl(appVariables['terms-of-distribution-url']);
        setTouUrl(appVariables['terms-of-use-url']);
        setPpUrl(appVariables['privacy-policy-url']);
      }
    }
  }, [appVariables]);

  useEffect(() => {
    if (!currentUser || Object.keys(currentUser).length === 0) {
      return;
    }
    if (isDemoLocked()) {
      setCanAddTakedown(currentUser.can_add_takedown !== false);
      return;
    }
    setCanAddTakedown(Boolean(currentUser.can_add_takedown));
  }, [currentUser]);


  useEffect(() => {
    if (!didEff1.current) {
      didEff1.current = true;
      return;
    }
    setForceUpdate(prev => prev + 1);
  }, [currrentPage, pageSize, ordering.order, ordering.name, subUser, statusFilter]);


  useEffect(() => {
    if (!didEff2.current) {
      didEff2.current = true;
      return;
    }
    const timeout = setTimeout(() => {
      setForceUpdate(prev => prev + 1);
    }, 500)
    return () => clearTimeout(timeout)
  }, [query])

  const defaultSorted = [{
    dataField: ordering.sort,
    order: ordering.order === "-" ? "desc" : "asc"
  }] 
  
  const tableProps = {
    tableData,
    defaultSorted,
    forceUpdate,
    setForceUpdate
  };

  const handleReleaseLayoutChange = (nextLayout) => {
    if (!isReleaseLayoutToggleEnabled(layoutConfig)) {
      return;
    }
    setLayoutView(nextLayout);
    setPageSize(getDefaultPageSizeForLayout(nextLayout));
    setCurrentPage(1);
  };

  const paginationProps = {
    pageSize,
    setPageSize,
    dataCount,
    setCurrentPage,
    currrentPage,
    pageSizeOptions: getPageSizeOptionsForLayout(layoutView),
  }
  return (
    <>
      {loading ? (
        <TableHelper loading />
      ) : error ? (
        <TableHelper error />
      ) :  (
        <div>
          {
            (currentUser && currentUser.is_blacklisted) &&
            <>
              <Alert color="danger">
                Your account has been blacklisted after review as we have found that the information associated with your account is linked with one of the internationally blacklisted users in our system. Consequently, we have disabled the Distribution feature for your account.
              </Alert>
            </>
          }
          {
            (!currentUser.is_blacklisted && currentUser.distribution && !currentUser.unlimited_track_amount && (currentUser.total_allowed_track_amount <= 0)) &&  <Alert color="danger">
            You have spent your track distribution allowance. In order to upload more tracks please upgrade your account or contact our support team.
          </Alert>
          }
          { 
            (!currentUser.is_blacklisted && !currentUser.distribution) &&
            <ModuleAccessDeniedAlert module="distribution" />
          }
          {
            (!currentUser.is_blacklisted && !currentUser.release_transfer) &&
            <Alert color="danger">
              Currently, you do not have permission to use release transfer feature. If you are transferring a bigger back catalog or wish to transfer more record labels catalogs with just a few clicks, don't hesitate to contact our support team.
            </Alert>
          }
          {
            (currentUser.distribution) &&
            <>
              <ReleaseTable 
                {...tableProps} 
                layoutView={layoutView}
                layoutToggleEnabled={isReleaseLayoutToggleEnabled(layoutConfig)}
                onLayoutChange={handleReleaseLayoutChange}
                searchQuery={{
                  setQuery: handleSearchQueryChange,
                  query,
                  ordering,
                  setOrdering,
                  status: statusFilter,
                  setStatus: (nextStatus) => {
                    setStatusFilter(nextStatus);
                    setCurrentPage(1);
                  },
                }}
                subUserData={{subUser, setSubUser, subUserEndpoint, setSubUserEndpoint, setParentUserOnly}}
                todUrl={todUrl}
                touUrl={touUrl}
                ppUrl={ppUrl}
                canAddTakedown={canAddTakedown}
              />
              <Pagination pagination={paginationProps}/>
            </>
          }
        </div>
      ) }
    </>
  );
};

export default Releases;
